import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, ArrowLeft, Plus, Loader2, KeyRound, Trash2, X, ToggleLeft, ToggleRight, ShieldOff,
  Copy, Shield, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

const ACCESS_URL = "https://sistemadawest.com.br/login";

function credText(login, senha) {
  return `West Brasil — acesso: ${ACCESS_URL}\nLogin: ${login}\nSenha: ${senha}`;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // fallback
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); document.body.removeChild(ta); return true; }
    catch { document.body.removeChild(ta); return false; }
  }
}

export default function Usuarios() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novoOpen, setNovoOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [resetOpen, setResetOpen] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ login: "", name: "", password: "" });
  const [resetPwd, setResetPwd] = useState("");
  const [credenciais, setCredenciais] = useState(null); // {login, senha}
  const [permOpen, setPermOpen] = useState(null); // usuário sendo editado
  const [permRegistry, setPermRegistry] = useState(null); // {modulos: {...}}
  const [permSel, setPermSel] = useState([]);
  const [permAcc, setPermAcc] = useState({}); // {modulo: bool}
  const [permSaving, setPermSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setLista(r.data);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.is_master) {
      navigate("/configuracoes");
      return;
    }
    load();
  }, [user, navigate]);

  const criar = async (e) => {
    e.preventDefault();
    if (!form.login.trim() || !form.name.trim() || form.password.length < 6) {
      return toast.error("Preencha todos os campos (senha mínima 6 caracteres)");
    }
    setSaving(true);
    try {
      await api.post("/users", {
        login: form.login.trim().toLowerCase(),
        name: form.name.trim(),
        provisional_password: form.password,
        permissoes: [],
      });
      toast.success(`Usuário ${form.name} criado.`);
      setCredenciais({ login: form.login.trim().toLowerCase(), senha: form.password });
      setForm({ login: "", name: "", password: "" });
      setNovoOpen(false);
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const copiarCredenciais = async () => {
    if (!credenciais) return;
    const ok = await copyToClipboard(credText(credenciais.login, credenciais.senha));
    if (ok) toast.success("Credenciais copiadas!");
    else toast.error("Não consegui copiar. Copie manualmente.");
  };

  const toggleAtivo = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Usuário desativado" : "Usuário ativado");
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    }
  };

  const resetar = async () => {
    if (resetPwd.length < 6) return toast.error("Senha mínima 6 caracteres");
    setSaving(true);
    try {
      await api.post(`/users/${resetOpen.id}/reset-password`, { provisional_password: resetPwd });
      toast.success(`Senha de ${resetOpen.name} redefinida.`);
      setCredenciais({ login: resetOpen.login, senha: resetPwd });
      setResetOpen(null);
      setResetPwd("");
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const abrirPermissoes = async (u) => {
    setPermOpen(u);
    setPermSel([...(u.permissoes || [])]);
    if (!permRegistry) {
      try {
        const r = await api.get("/permissoes/registry");
        setPermRegistry(r.data);
        const acc = {};
        Object.keys(r.data.modulos || {}).forEach((m) => { acc[m] = true; });
        setPermAcc(acc);
      } catch (e) {
        {const _m=apiError(e); if(_m) toast.error(_m);};
      }
    }
  };

  const togglePerm = (key) => {
    setPermSel((s) => s.includes(key) ? s.filter((k) => k !== key) : [...s, key]);
  };

  const toggleModulo = (modulo, keys) => {
    const allOn = keys.every((k) => permSel.includes(k));
    setPermSel((s) => allOn ? s.filter((k) => !keys.includes(k)) : [...new Set([...s, ...keys])]);
  };

  const salvarPermissoes = async () => {
    if (!permOpen) return;
    setPermSaving(true);
    try {
      await api.put(`/users/${permOpen.id}`, { permissoes: permSel });
      toast.success(`Permissões de ${permOpen.name} salvas`);
      setPermOpen(null);
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setPermSaving(false);
    }
  };

  const excluir = async () => {
    setSaving(true);
    try {
      await api.delete(`/users/${confirmDel.id}`);
      toast.success(`Usuário ${confirmDel.name} excluído`);
      setConfirmDel(null);
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "h-11 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500";

  return (
    <div data-testid="usuarios-page" className="mx-auto max-w-4xl">
      <button
        onClick={() => navigate("/configuracoes")}
        className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        data-testid="usuarios-voltar"
      >
        <ArrowLeft size={14} /> Voltar
      </button>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            <Users size={26} /> Usuários
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Gerencie quem tem acesso ao sistema. As permissões detalhadas serão liberadas na Fase 2 (nova usuário criado começa <strong>sem nenhuma permissão</strong>).
          </p>
        </div>
        <button
          onClick={() => setNovoOpen(true)}
          data-testid="usuarios-novo"
          className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
        >
          <Plus size={16} /> Novo usuário
        </button>
      </div>

      <div className="overflow-x-auto rounded-md border-2 border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Login</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Permissões</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="usuarios-table">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                  <Loader2 className="mx-auto animate-spin" />
                </td>
              </tr>
            )}
            {!loading && lista.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">
                  Nenhum usuário cadastrado ainda. Clique em <strong>Novo usuário</strong> para começar.
                </td>
              </tr>
            )}
            {!loading &&
              lista.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  data-testid={`usuario-row-${u.login}`}
                >
                  <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{u.login}</td>
                  <td className="px-4 py-3">
                    {u.is_active ? (
                      <span className="rounded-sm bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                        Ativo
                      </span>
                    ) : (
                      <span className="rounded-sm bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">
                        Desativado
                      </span>
                    )}
                    {u.must_change_password && (
                      <span className="ml-2 rounded-sm bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        Senha provisória
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {u.permissoes.length === 0 ? (
                      <span className="inline-flex items-center gap-1 text-slate-400">
                        <ShieldOff size={12} /> nenhuma
                      </span>
                    ) : (
                      `${u.permissoes.length} liberada${u.permissoes.length === 1 ? "" : "s"}`
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => abrirPermissoes(u)}
                        data-testid={`usuario-permissoes-${u.login}`}
                        className="flex items-center gap-1 rounded-md border-2 border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        <Shield size={14} /> Permissões
                      </button>
                      <button
                        onClick={() => toggleAtivo(u)}
                        data-testid={`usuario-toggle-${u.login}`}
                        title={u.is_active ? "Desativar" : "Ativar"}
                        className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        {u.is_active ? <ToggleRight size={16} className="text-green-600" /> : <ToggleLeft size={16} className="text-slate-400" />}
                        {u.is_active ? "Desativar" : "Ativar"}
                      </button>
                      <button
                        onClick={() => setResetOpen(u)}
                        data-testid={`usuario-reset-${u.login}`}
                        className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        <KeyRound size={14} /> Resetar senha
                      </button>
                      <button
                        onClick={() => setConfirmDel(u)}
                        data-testid={`usuario-delete-${u.login}`}
                        className="flex items-center gap-1 rounded-md border-2 border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"
                      >
                        <Trash2 size={14} /> Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Modal Novo Usuário */}
      {novoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setNovoOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-md bg-white p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="usuario-novo-modal"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Novo usuário</h3>
              <button onClick={() => setNovoOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={criar} className="space-y-3">
              <label className="block text-sm font-semibold text-slate-700">
                Nome
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="novo-nome"
                  required
                  className={`mt-1 font-normal ${inputCls}`}
                />
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                Login
                <input
                  value={form.login}
                  onChange={(e) => setForm((f) => ({ ...f, login: e.target.value.toLowerCase() }))}
                  data-testid="novo-login"
                  required
                  placeholder="ex: diego"
                  className={`mt-1 font-normal font-mono ${inputCls}`}
                />
                <div className="mt-1 text-xs text-slate-400">Use minúsculas, sem espaços. Será o login dele.</div>
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                Senha provisória
                <PasswordInput
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  data-testid="novo-senha"
                  required
                  minLength={6}
                  inputClassName={`mt-1 font-normal font-mono ${inputCls} pr-11`}
                  className="mt-1"
                />
                <div className="mt-1 text-xs text-slate-400">
                  Passe essa senha para o usuário. Ele será obrigado a trocar no primeiro login.
                </div>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setNovoOpen(false)}
                  className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="novo-criar"
                  className="flex items-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving && <Loader2 className="animate-spin" size={14} />}
                  Criar usuário
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Reset Senha */}
      {resetOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setResetOpen(null)}
        >
          <div
            className="w-full max-w-md rounded-md bg-white p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="usuario-reset-modal"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Resetar senha de {resetOpen.name}</h3>
              <button onClick={() => setResetOpen(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>
            <label className="block text-sm font-semibold text-slate-700">
              Nova senha provisória
              <PasswordInput
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                data-testid="reset-senha"
                minLength={6}
                autoFocus
                inputClassName={`mt-1 font-normal font-mono ${inputCls} pr-11`}
                className="mt-1"
              />
              <div className="mt-1 text-xs text-slate-400">
                O usuário será obrigado a trocar no próximo login.
              </div>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setResetOpen(null)}
                className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-100"
              >
                Cancelar
              </button>
              <button
                onClick={resetar}
                disabled={saving}
                data-testid="reset-confirmar"
                className="flex items-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving && <Loader2 className="animate-spin" size={14} />}
                Resetar senha
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Credenciais (após criar/resetar) */}
      {credenciais && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setCredenciais(null)}
        >
          <div
            className="w-full max-w-md rounded-md bg-white p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="credenciais-modal"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Acesso pronto para copiar</h3>
              <button onClick={() => setCredenciais(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>
            <pre
              className="mb-3 whitespace-pre-wrap rounded-md border-2 border-slate-200 bg-slate-50 p-3 text-sm text-slate-800"
              data-testid="credenciais-texto"
            >{credText(credenciais.login, credenciais.senha)}</pre>
            <p className="mb-4 text-xs text-slate-500">
              Cole no WhatsApp e envie para o usuário. Ele precisará trocar a senha no primeiro login.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCredenciais(null)}
                className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-100"
              >
                Fechar
              </button>
              <button
                onClick={copiarCredenciais}
                data-testid="credenciais-copiar"
                className="flex items-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
              >
                <Copy size={14} /> Copiar credenciais
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Permissões (accordion) */}
      {permOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPermOpen(null)}
        >
          <div
            className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-md bg-white p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="permissoes-modal"
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                  <Shield size={18} /> Permissões de {permOpen.name}
                </h3>
                <p className="text-xs text-slate-500">Login: <span className="font-mono">{permOpen.login}</span></p>
              </div>
              <button onClick={() => setPermOpen(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>
            {!permRegistry ? (
              <div className="flex justify-center py-10 text-slate-400"><Loader2 className="animate-spin" /></div>
            ) : (
              <div className="space-y-2" data-testid="permissoes-accordion">
                {Object.entries(permRegistry.modulos).map(([modulo, items]) => {
                  const keys = items.map((i) => i.key);
                  const allOn = keys.every((k) => permSel.includes(k));
                  const someOn = keys.some((k) => permSel.includes(k));
                  const open = permAcc[modulo];
                  return (
                    <div key={modulo} className="rounded-md border-2 border-slate-200 bg-white" data-testid={`perm-modulo-${modulo}`}>
                      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setPermAcc((a) => ({ ...a, [modulo]: !a[modulo] }))}
                          className="flex flex-1 items-center gap-1 text-left text-sm font-bold text-slate-800"
                        >
                          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          {modulo}
                          <span className="ml-2 text-xs font-normal text-slate-500">
                            {keys.filter((k) => permSel.includes(k)).length}/{keys.length}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleModulo(modulo, keys)}
                          data-testid={`perm-toggle-modulo-${modulo}`}
                          className={`rounded-md border-2 px-2 py-1 text-xs font-semibold ${
                            allOn ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                  : "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                          }`}
                        >
                          {allOn ? "Desligar tudo" : someOn ? "Ligar tudo" : "Ligar tudo"}
                        </button>
                      </div>
                      {open && (
                        <ul className="divide-y divide-slate-100">
                          {items.map((it) => {
                            const on = permSel.includes(it.key);
                            return (
                              <li key={it.key} className="flex items-start gap-3 px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => togglePerm(it.key)}
                                  data-testid={`perm-toggle-${it.key}`}
                                  className={`mt-0.5 flex h-5 w-9 items-center rounded-full transition-colors ${on ? "bg-green-500 justify-end" : "bg-slate-300 justify-start"}`}
                                >
                                  <span className="mx-0.5 h-4 w-4 rounded-full bg-white shadow" />
                                </button>
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-slate-800">{it.label}</div>
                                  {it.desc && <div className="text-xs text-slate-500">{it.desc}</div>}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPermOpen(null)}
                className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-100"
              >
                Cancelar
              </button>
              <button
                onClick={salvarPermissoes}
                disabled={permSaving}
                data-testid="permissoes-salvar"
                className="flex items-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {permSaving && <Loader2 className="animate-spin" size={14} />}
                Salvar permissões
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="usuario-delete-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Vai excluir permanentemente <strong>{confirmDel?.name}</strong> ({confirmDel?.login}). Ele perderá o acesso na hora e não poderá mais logar. Você pode preferir <strong>desativar</strong> em vez de excluir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <button
              onClick={() => setConfirmDel(null)}
              className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-100"
            >
              Cancelar
            </button>
            <button
              onClick={excluir}
              disabled={saving}
              data-testid="usuario-delete-confirmar"
              className="flex items-center gap-1 rounded-md bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="animate-spin" size={14} />}
              <Trash2 size={14} /> Excluir permanentemente
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
